import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { theme, card, btnPrimary, btnSecondary, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useExperience } from "../context/ExperienceContext";
import { useFrameworks } from "../context/FrameworksContext";
import { useCurrentUser } from "../auth/useCurrentUser";
import { FrameworkControlList } from "../components/FrameworkControlList";
import type { SoaRow, EvidenceDoc, ControlGroupDef } from "../components/FrameworkControlList";
import { buildInsightsMap } from "../components/ControlInsightsPanel";
import type { ControlInsights } from "../components/ControlInsightsPanel";
import { StandardHeader } from "../components/standards/StandardHeader";
import { frameworkAccent } from "../components/controlStatus";

// PCI DSS v4.0, one page per Self-Assessment Questionnaire. The SAQ decides which of the twelve
// principal requirements are in scope; the requirements sit on the shared assessment list,
// grouped by the six PCI DSS goals.

const SAQ_CONFIGS: Record<string, { label: string; reqs: number[]; description: string }> = {
  "saq-a": {
    label: "SAQ A",
    reqs: [2, 6, 8, 9, 10, 12],
    description: "Card-not-present merchants who have fully outsourced all cardholder data functions to PCI-compliant third parties.",
  },
  "saq-a-ep": {
    label: "SAQ A-EP",
    reqs: [1, 2, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    description: "E-commerce merchants who partially outsource payment processing but control how cardholders access the payment page.",
  },
  "saq-b": {
    label: "SAQ B",
    reqs: [1, 2, 6, 8, 9, 10, 11, 12],
    description: "Merchants using only imprint machines or standalone dial-out terminals with no electronic storage of cardholder data.",
  },
  "saq-b-ip": {
    label: "SAQ B-IP",
    reqs: [1, 2, 4, 6, 7, 8, 9, 10, 11, 12],
    description: "Merchants using only standalone IP-connected PTS-approved POI terminals with no electronic storage of cardholder data.",
  },
  "saq-c": {
    label: "SAQ C",
    reqs: [1, 2, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    description: "Merchants with payment application systems connected to the internet, no electronic storage of cardholder data.",
  },
  "saq-c-vt": {
    label: "SAQ C-VT",
    reqs: [1, 2, 4, 5, 6, 8, 9, 10, 11, 12],
    description: "Merchants who manually enter single transactions via a web-based virtual payment terminal solution.",
  },
  "saq-p2pe": {
    label: "SAQ P2PE",
    reqs: [3, 9, 10, 12],
    description: "Merchants using only hardware payment terminals within a PCI SSC-validated Point-to-Point Encryption solution.",
  },
  "saq-d-merchant": {
    label: "SAQ D (Merchant)",
    reqs: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    description: "All other SAQ-eligible merchants not included in other SAQ types.",
  },
  "saq-d-sp": {
    label: "SAQ D (Service Provider)",
    reqs: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    description: "Service providers eligible to complete a Self-Assessment Questionnaire.",
  },
};

// The six goals in the standard's own words and capitalisation (PCI DSS v4.0 overview).
const PCI_GOALS: Array<{ title: string; reqs: number[] }> = [
  { title: "Build and Maintain a Secure Network and Systems", reqs: [1, 2] },
  { title: "Protect Account Data", reqs: [3, 4] },
  { title: "Maintain a Vulnerability Management Program", reqs: [5, 6] },
  { title: "Implement Strong Access Control Measures", reqs: [7, 8, 9] },
  { title: "Regularly Monitor and Test Networks", reqs: [10, 11] },
  { title: "Maintain an Information Security Policy", reqs: [12] },
];

function reqNumFromControlId(id: string): number | null {
  const m = id.match(/pci-req-(\d+)$/);
  return m ? parseInt(m[1], 10) : null;
}

const GOAL_GROUPS: ControlGroupDef[] = PCI_GOALS.map((g, i) => ({
  key: `goal-${i + 1}`,
  title: g.title,
  hint: `Requirements ${g.reqs.join(", ")}`,
  match: (r) => { const n = reqNumFromControlId(r.control.id); return n !== null && g.reqs.includes(n); },
}));

export function PciDssPage() {
  const { novice } = useExperience();
  const { pciSaqTypes, reload: reloadFrameworks } = useFrameworks();
  const { saqType = "saq-a" } = useParams<{ saqType: string }>();
  const [rows, setRows] = useState<SoaRow[]>([]);
  const [docs, setDocs] = useState<EvidenceDoc[]>([]);
  const [insightsMap, setInsightsMap] = useState<Map<string, ControlInsights>>(new Map());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const saq = SAQ_CONFIGS[saqType] ?? SAQ_CONFIGS["saq-a"];
  const isActiveSaq = pciSaqTypes.includes(saqType);
  const hasAnySaqSelected = pciSaqTypes.length > 0;
  const accent = frameworkAccent("pci_dss");

  async function loadAll() {
    const [soaData, docsData, latestRun] = await Promise.all([
      api.get<{ soa: SoaRow[] }>("/controls/soa"),
      api.get<{ documents: EvidenceDoc[] }>("/documents"),
      api.get<{ run: { resultJson: unknown } | null }>("/guidance/latest").catch(() => ({ run: null })),
    ]);
    setRows(soaData.soa.filter(r => r.control.frameworkId === "pci_dss" && r.control.kind === "CLAUSE"));
    setDocs(docsData.documents);
    if (latestRun.run?.resultJson) setInsightsMap(buildInsightsMap(latestRun.run.resultJson));
  }

  useEffect(() => { loadAll(); }, []);

  // The organisation can hold several SAQs at once: adding or removing this one
  // keeps the rest of the current selection.
  async function saveSaqTypes(saqTypes: string[], fallback: string) {
    setSaving(true);
    setSaveError(null);
    try {
      await api.put("/organizations/frameworks/pci_dss/saq", { saqTypes });
      reloadFrameworks();
    } catch (err) {
      setSaveError(apiErrorMessage(err, fallback));
    } finally {
      setSaving(false);
    }
  }

  const { user } = useCurrentUser();
  const canChooseSaq = user?.role !== "client" || !!user?.permissions?.includes("org:admin");

  function selectThisSaq() {
    return saveSaqTypes([...new Set([...pciSaqTypes, saqType])], "Could not select this SAQ.");
  }

  function clearSaq() {
    return saveSaqTypes(pciSaqTypes.filter((t) => t !== saqType), "Could not remove this SAQ.");
  }

  const filteredRows = rows.filter(r => {
    const n = reqNumFromControlId(r.control.id);
    return n !== null && saq.reqs.includes(n);
  });

  // Choosing SAQs is an organisation-administrator change on the server (org:admin);
  // other members see which SAQ is active but not the buttons.
  const saqButtons = !canChooseSaq ? (isActiveSaq ? (
    <span style={{ ...text.meta, fontWeight: 600, padding: "4px 10px", borderRadius: 3, background: theme.color.accentBg, color: theme.color.accentText, border: `1px solid ${theme.color.accent}` }}>Your active SAQ</span>
  ) : null) : isActiveSaq ? (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ ...text.meta, fontWeight: 600, padding: "4px 10px", borderRadius: 3, background: theme.color.accentBg, color: theme.color.accentText, border: `1px solid ${theme.color.accent}` }}>
        Your active SAQ
      </span>
      <button type="button" onClick={clearSaq} disabled={saving} style={{ ...btnSecondary, padding: "5px 10px", fontSize: text.meta.fontSize }}>
        {saving ? "Saving…" : "Remove this SAQ"}
      </button>
    </div>
  ) : (
    <button type="button" onClick={selectThisSaq} disabled={saving} style={{ ...btnPrimary, padding: "8px 16px", fontSize: text.hint.fontSize, opacity: saving ? 0.6 : 1 }}>
      {saving ? "Saving…" : hasAnySaqSelected ? "Add this SAQ" : "Use this SAQ"}
    </button>
  );

  const saqControls = (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
      {saqButtons}
      {saveError && <div role="alert" style={{ ...text.meta, color: theme.color.danger }}>{saveError}</div>}
    </div>
  );

  return (
    <div className="page-pad">
      <StandardHeader frameworkId="pci_dss" page={saq.label} subtitle={saq.description} aside={saqControls} />

      {/* The SAQ's scope: which principal requirements it asks about */}
      <div style={{ ...card, borderLeft: `4px solid ${accent}`, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24 }}>
        <div>
          <div style={{ ...text.label, marginBottom: 3 }}>Requirements in this SAQ</div>
          <div style={text.hint}>Req{saq.reqs.length === 1 ? "" : "s"} {saq.reqs.join(", ")}</div>
        </div>
        <div style={{ flexShrink: 0, textAlign: "right" }}>
          <div style={text.stat}>{saq.reqs.length}</div>
          <div style={text.meta}>of 12 requirements</div>
        </div>
      </div>

      <FrameworkControlList
        rows={filteredRows}
        docs={docs}
        insightsMap={insightsMap}
        frameworkId="pci_dss"
        loadAll={loadAll}
        novice={novice}
        groups={GOAL_GROUPS}
        noun="requirement"
        summaryLabel="Requirements met"
        groupLabel="All goals"
      />
    </div>
  );
}
